import { useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { toastService } from '../../../../utils/toastService';
import {
    useGetVehicleOrderBrandsQuery,
    useLazyGetVehicleOrderModelsQuery,
    useLazyGetVehicleOrderMaterialsQuery,
    useGetVehicleOrderPaymentTermsQuery,
    useGetVehicleOrderInitialDataQuery,
    useCreateVehicleOrderDraftMutation
} from '../../../../api/endpoints/vehicleOrder';

const useVehiclePlaceOrder = () => {
    const navigate = useNavigate();
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const [brand, setBrand] = useState(null);
    const [model, setModel] = useState(null);
    const [matnr, setMatnr] = useState(null);
    const [maktx, setMaktx] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [deliveryDate, setDeliveryDate] = useState(null);
    const [paymentTerms, setPaymentTerms] = useState(null);
    const [paymentDate, setPaymentDate] = useState(null);
    const [validationMessageKey, setValidationMessageKey] = useState('');

    const { data: brands = [] } = useGetVehicleOrderBrandsQuery({ languageId });
    const { data: paymentTermsList = [], isLoading: paymentTermsLoading } = useGetVehicleOrderPaymentTermsQuery({ languageId });
    const { data: initialData, isLoading: initialLoading } = useGetVehicleOrderInitialDataQuery({ languageId });

    const [fetchModels, { data: models = [], isFetching: modelsLoading }] = useLazyGetVehicleOrderModelsQuery();
    const [fetchMaterials, { data: materials = [], isFetching: materialsLoading }] = useLazyGetVehicleOrderMaterialsQuery();

    const [createDraft, { isLoading: submitting }] = useCreateVehicleOrderDraftMutation();

    const minDate = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);

    useEffect(() => {
        if (brand) {
            fetchModels({ brand, languageId });
        }
    }, [brand, fetchModels, languageId]);

    useEffect(() => {
        if (brand && model) {
            fetchMaterials({ brand, model, languageId });
        }
    }, [brand, model, fetchMaterials, languageId]);

    const onBrandChange = useCallback((e) => {
        setBrand(e.value);
        setModel(null);
        setMatnr(null);
        setMaktx('');
        setValidationMessageKey('');
    }, []);

    const onModelChange = useCallback((e) => {
        setModel(e.value);
        setMatnr(null);
        setMaktx('');
        setValidationMessageKey('');
    }, []);

    const onMaterialChange = useCallback(
        (e) => {
            const value = e.value;
            setMatnr(value);
            const row = materials.find((m) => m.matnr === value);
            setMaktx(row?.maktx ?? '');
            setValidationMessageKey('');
        },
        [materials]
    );

    const getValidationMessageKey = useCallback(() => {
        if (!brand) return 'vehicleSalesPlaceOrder.validationBrand';
        if (!model) return 'vehicleSalesPlaceOrder.validationModel';
        if (!matnr) return 'vehicleSalesPlaceOrder.validationMaterial';
        const qtyNum = Number(quantity);
        if (quantity == null || !Number.isFinite(qtyNum) || qtyNum < 1 || !Number.isInteger(qtyNum)) {
            return 'vehicleSalesPlaceOrder.validationQuantity';
        }
        if (!deliveryDate) return 'vehicleSalesPlaceOrder.validationDeliveryDate';
        if (!paymentTerms) return 'vehicleSalesPlaceOrder.validationPaymentTerms';
        if (!paymentDate) return 'vehicleSalesPlaceOrder.validationPaymentDate';
        const dDel = new Date(deliveryDate);
        const dPay = new Date(paymentDate);
        dDel.setHours(0, 0, 0, 0);
        dPay.setHours(0, 0, 0, 0);
        if (dDel < minDate) return 'vehicleSalesPlaceOrder.validationDeliveryPast';
        if (dPay < minDate) return 'vehicleSalesPlaceOrder.validationPaymentPast';
        return '';
    }, [brand, model, matnr, quantity, deliveryDate, paymentTerms, paymentDate, minDate]);

    const modelOptions = brand ? models : [];
    const materialOptions = brand && model ? materials : [];

    const handleSubmit = useCallback(async () => {
        const key = getValidationMessageKey();
        if (key) {
            setValidationMessageKey(key);
            return;
        }
        setValidationMessageKey('');
        const body = {
            brand,
            model,
            matnr,
            maktx,
            deliveryDate: deliveryDate instanceof Date ? deliveryDate.toISOString() : deliveryDate,
            quantity: Math.floor(Number(quantity)),
            paymentTerms,
            paymentDate: paymentDate instanceof Date ? paymentDate.toISOString() : paymentDate,
            languageId
        };
        try {
            const res = await createDraft(body).unwrap();
            if (res?.isSuccessful && res?.httpStatusCode === 201) {
                toastService.success(th('vehicleSalesPlaceOrder.draftCreated'), th('common.success'), 4000);
                navigate('/vehicle-sales/order-requests');
            }
        } catch {
            /* baseQueryWithReauth hata toast */
        }
    }, [
        getValidationMessageKey,
        createDraft,
        brand,
        model,
        matnr,
        maktx,
        quantity,
        paymentTerms,
        deliveryDate,
        paymentDate,
        languageId,
        th,
        navigate
    ]);

    return {
        th,
        brands,
        paymentTermsList,
        paymentTermsLoading,
        initialData,
        initialLoading,
        brand,
        model,
        matnr,
        quantity,
        deliveryDate,
        paymentTerms,
        paymentDate,
        modelOptions,
        materialOptions,
        modelsLoading,
        materialsLoading,
        minDate,
        submitting,
        validationMessageKey,
        onBrandChange,
        onModelChange,
        onMaterialChange,
        setQuantity,
        setDeliveryDate,
        setPaymentTerms,
        setPaymentDate,
        handleSubmit
    };
};

export default useVehiclePlaceOrder;
